import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@timeghost/ui/shadcn/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@timeghost/ui/shadcn/select";
import { type JSX, useState } from "react";
import { useTranslation } from "react-i18next";
import AdminInfiniteTablePage from "@/components/admin/admin-infinite-table-page";
import AdminNotificationsTable, { ADMIN_NOTIFICATION_GRID_CLASS } from "@/components/admin/admin-notifications-table";
import AdminTableLoader from "@/components/admin/admin-table-loader";
import CreateAdminNotificationDialog, {
	toNotificationCreateBody,
	toNotificationPatchBody,
} from "@/components/admin/create-admin-notification-dialog";
import { useDebouncedListSearch } from "@/lib/hooks/use-debounced-list-search";
import { useHeldDialogEntity } from "@/lib/hooks/use-held-dialog-entity";
import stores from "@/lib/stores/web-api-stores";
import { useAdminNotification } from "@/queries/admin/use-admin-item.query";
import {
	flattenAdminListPages,
	loadNextAdminPage,
	useAdminNotificationsList,
} from "@/queries/admin/use-admin-list.query";

const AdminNotificationsPage = (): JSX.Element => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const { search, listSearch, onSearchChange, flushSearch } = useDebouncedListSearch();
	const [scope, setScope] = useState("");
	const [createOpen, setCreateOpen] = useState(false);
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const query = useAdminNotificationsList(listSearch, scope);
	const itemQuery = useAdminNotification(selectedId);
	const items = flattenAdminListPages(query.data);
	const liveItem = createOpen
		? null
		: selectedId && itemQuery.data?.id === selectedId
			? itemQuery.data
			: (items.find((item) => item.id === selectedId) ?? null);
	const canLoadMore = query.hasNextPage && !query.isFetchingNextPage;
	const sheetOpen = createOpen || selectedId !== null;
	const { held, onOpenChangeComplete } = useHeldDialogEntity(sheetOpen, liveItem);
	const invalidate = async (): Promise<void> => {
		await queryClient.invalidateQueries({ queryKey: ["admin", "notifications"] });
	};
	const saveMutation = useMutation({
		mutationFn: async (body: { readonly id?: string; readonly payload: Record<string, unknown> }) => {
			if (body.id) {
				await stores.api.patch(`/platform-admin/notifications/${body.id}`, body.payload);
				return;
			}
			await stores.api.post("/platform-admin/notifications", body.payload);
		},
		onSuccess: invalidate,
	});
	const deleteMutation = useMutation({
		mutationFn: async (id: string) => {
			await stores.api.delete(`/platform-admin/notifications/${id}`);
		},
		onSuccess: invalidate,
	});
	const editing = selectedId !== null;

	return (
		<>
			<AdminInfiniteTablePage
				isEmpty={items.length === 0}
				isError={query.isError}
				isPending={query.isPending}
				loader={<AdminTableLoader columnCount={5} gridClass={ADMIN_NOTIFICATION_GRID_CLASS} />}
				onSearchChange={onSearchChange}
				onSearchFlush={flushSearch}
				search={search}
				searchPlaceholder={t("admin.notifications.searchTitle")}
				title={t("nav.adminNotifications")}
				toolbar={
					<div className="flex flex-wrap items-center gap-2">
						<Select
							onValueChange={(value) => setScope(typeof value === "string" && value !== "all" ? value : "")}
							value={scope || "all"}>
							<SelectTrigger className="w-44">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">{t("admin.notifications.scopeAll")}</SelectItem>
								<SelectItem value="global">{t("admin.notifications.scopeGlobal")}</SelectItem>
								<SelectItem value="workspace">{t("admin.notifications.scopeWorkspace")}</SelectItem>
								<SelectItem value="user">{t("admin.notifications.scopeUser")}</SelectItem>
							</SelectContent>
						</Select>
						<Button
							onClick={() => {
								setSelectedId(null);
								setCreateOpen(true);
							}}
							size="sm"
							type="button">
							{t("admin.notifications.createTitle")}
						</Button>
					</div>
				}>
				<AdminNotificationsTable
					isFetchingNextPage={query.isFetchingNextPage}
					items={items}
					nearEndEnabled={canLoadMore}
					onNearEnd={() => loadNextAdminPage(query)}
					onOpen={(item) => {
						setCreateOpen(false);
						setSelectedId(item.id);
					}}
				/>
			</AdminInfiniteTablePage>
			<CreateAdminNotificationDialog
				isPending={saveMutation.isPending || deleteMutation.isPending || (editing && itemQuery.isPending)}
				item={held}
				onDelete={
					selectedId
						? async () => {
								await deleteMutation.mutateAsync(selectedId);
							}
						: undefined
				}
				onOpenChange={(open) => {
					if (open) {
						return;
					}
					setCreateOpen(false);
					setSelectedId(null);
				}}
				onOpenChangeComplete={(open) => {
					onOpenChangeComplete(open);
					if (!open) {
						setCreateOpen(false);
						setSelectedId(null);
					}
				}}
				onSave={async (values) => {
					if (selectedId) {
						const etag = held?.etag;
						if (!etag) {
							throw new Error("admin.notFound");
						}
						await saveMutation.mutateAsync({
							id: selectedId,
							payload: toNotificationPatchBody(values, etag),
						});
						return;
					}
					await saveMutation.mutateAsync({ payload: toNotificationCreateBody(values) });
				}}
				open={createOpen || selectedId !== null}
			/>
		</>
	);
};

export const Route = createFileRoute("/_app/admin/notifications/")({
	component: AdminNotificationsPage,
});
